import {createContext, useContext, type ComponentProps, type ReactNode} from 'react';
import {cn} from '../lib/utils';
import {ACCENT_RAIL, type AccentColor} from '../lib/color';
import type {Testable} from '../lib/testable';
import {Typography} from '../Typography';

export type CardProps = ComponentProps<'div'> & Testable & {active?: AccentColor | false};

export const Card = ({
	active, testId, className, children, ...props
}: CardProps) => (
	<CardContext.Provider value={true}>
		<div
			data-component='Card'
			data-testid={testId}
			className={cn(
				'relative flex w-full shrink-0 flex-col items-start gap-6 break-words ' +
				'rounded-lg bg-background-800 p-6 shadow-l',
				'border-l-[6px]',
				active ? ACCENT_RAIL[active] : 'border-l-transparent',
				className
			)}
			{...props}>
			{children}
		</div>
	</CardContext.Provider>
);

Card.displayName = 'Card';

export interface CardHeaderProps {
	title?: string;
	action?: ReactNode;
}

export const CardHeader = ({title, action}: CardHeaderProps) => {
	useInCard('CardHeader');

	if (!title && !action) return null;

	return (
		<>
			{title && <Typography data-component='CardTitle' variant='body2' color='textSecondary'>{title}</Typography>}
			{action && <div data-component='CardAction' className='absolute right-6 top-6'>{action}</div>}
		</>
	);
};

CardHeader.displayName = 'CardHeader';

export type CardContentProps = ComponentProps<'div'> & Testable;

export const CardContent = ({
	testId, className, children, ...props
}: CardContentProps) => {
	useInCard('CardContent');

	return (
		<div data-component='CardContent' data-testid={testId} className={cn('w-full', className)} {...props}>
			{typeof children === 'string' ? <Typography variant='body3'>{children}</Typography> : children}
		</div>
	);
};

CardContent.displayName = 'CardContent';

export type CardFooterProps = ComponentProps<'div'> & Testable & {divider?: boolean};

export const CardFooter = ({
	divider = false, testId, className, children, ...props
}: CardFooterProps) => {
	useInCard('CardFooter');

	return (
		<div
			data-component='CardFooter'
			data-testid={testId}
			className={cn('mt-auto flex w-full items-center', divider && 'border-t border-foreground-400 pt-6', className)}
			{...props}>
			{children}
		</div>
	);
};

CardFooter.displayName = 'CardFooter';

const CardContext = createContext(false);

function useInCard(part: string) {
	const inCard = useContext(CardContext);
	if (!inCard) throw new Error(`${part} must be rendered inside Card.`);
}
